import React from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

/**
 * Dialog component for displaying integrity check results
 */
export const IntegrityCheckDialog = ({
    open,
    onClose,
    result,
    onProceed,
    onCancel,
    pendingShifts = []
}) => {
    if (!result) return null;

    const isSeriesCreation = pendingShifts.length > 1 && pendingShifts.some(shift => shift.recurrence);
    const title = isSeriesCreation ? "Series Creation Validation" : "Shift Copy Validation";
    const description = isSeriesCreation
        ? "The following issues were found when validating the recurring series. You can proceed with creation or cancel to make changes."
        : "The following issues were found when validating the copied shifts. You can proceed with copying or cancel to make changes.";

    const errorCount = result.issues.filter(issue => issue.severity === 'error').length;
    const warningCount = result.issues.filter(issue => issue.severity === 'warning').length;

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    {description}
                </DialogContentText>

                {(errorCount > 0 || warningCount > 0) && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {errorCount > 0 && `${errorCount} error(s)`}
                        {errorCount > 0 && warningCount > 0 && ', '}
                        {warningCount > 0 && `${warningCount} warning(s)`}
                    </Typography>
                )}

                <List>
                    {result.issues.map((issue, index) => (
                        <ListItem key={index} sx={{ px: 0 }}>
                            <Alert
                                severity={issue.severity}
                                sx={{ width: '100%', mb: 1 }}
                            >
                                <Typography variant="body2">
                                    {issue.message}
                                </Typography>
                                {issue.type === 'duplicate' && issue.conflicts && (
                                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                        Conflicts with: {issue.conflicts.map(conflict =>
                                            `"${conflict.name}" (${conflict.date})`
                                        ).join(', ')}
                                    </Typography>
                                )}
                            </Alert>
                        </ListItem>
                    ))}
                </List>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button onClick={onProceed} variant="contained">
                    {isSeriesCreation ? "Create Series" : "Copy Shifts"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};